import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  NgbCarousel,
  NgbCarouselConfig,
  NgbCarouselModule,
  NgbModule,
  NgbSlideEvent,
} from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { AnalyticsService } from '@digital-platform/rs-analytics/services';
import {
  ChannelsType,
  CmsContentService,
  CmsServiceAlertsService,
  mockServiceAlertsData,
} from '@digital-platform/rs-cms/services';
import { IconComponent } from '@digital-platform/rs-shared/ui-brand';

@Component({
  selector: 'digital-platform-alert-banner',
  templateUrl: './alert-banner.component.html',
  styleUrls: ['./alert-banner.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [CommonModule, NgbModule, NgbCarouselModule, IconComponent],
  animations: [
    trigger('accordionOpenClose', [
      transition(':enter', [
        style({ opacity: 0, height: 0 }),
        animate(
          '0.25s ease-out',
          style({ marginBottom: '-10px', opacity: 1, height: '*' })
        ),
      ]),
      transition(':leave', [
        animate('0.20s', style({ opacity: 0, height: 0 })),
      ]),
    ]),
  ],
})
export class AlertBannerComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  // Used for testing
  @Input() public limitSingleMessage = false;
  @Input() public useMockServiceAlertsData = false;

  private elementRef: ElementRef = inject(ElementRef);
  public config = inject(NgbCarouselConfig);
  public channels: ChannelsType[] = [];
  public channelsPrefiltered: ChannelsType[] = [];
  public currentSlide = 0;
  public showBanner = true;
  public showMobileAlerts = false;
  public isBrowser: boolean;
  private commentCreated = false;

  private cmsContentService = inject(CmsContentService);
  private cmsServiceAlertsService: CmsServiceAlertsService = inject(
    CmsServiceAlertsService
  );
  private analyticsService = inject(AnalyticsService);
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private destroyed$ = new Subject<void>();
  @ViewChild('carousel', { static: false }) carousel!: NgbCarousel;

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.config.showNavigationArrows = false;
    this.config.showNavigationIndicators = false;
  }

  // Used for testing
  public mockServiceAlertsData = mockServiceAlertsData;

  @HostListener('document:click', ['$event'])
  onBannerLinkClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.tagName.toLowerCase() === 'a' && target.closest('.alert-html')) {
      event.preventDefault();
      const href = target.getAttribute('href');
      if (href !== null) {
        this.bannerLinkAnalytics(href);
      }
    }
  }

  ngOnInit() {
    this.getAlerts();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.filterChannels();

        if (this.channels.length === 1) {
          this.showMobileAlerts = false;
        }
      });
  }

  ngAfterViewInit() {
    this.insertNoIndexComments();
  }

  ngOnChanges() {
    this.insertNoIndexComments();
  }

  private insertNoIndexComments() {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const htmlElement = this.elementRef.nativeElement as HTMLElement;
        if (htmlElement.parentNode) {
          if (this.commentCreated) {
            const { firstChild, lastChild } = htmlElement.parentNode;
            if (
              firstChild?.nodeName?.includes('comment') &&
              firstChild?.textContent?.includes('NOINDEX')
            ) {
              htmlElement.parentNode.removeChild(firstChild);
            }
            if (
              lastChild?.nodeName?.includes('comment') &&
              lastChild?.textContent?.includes('NOINDEX')
            ) {
              htmlElement.parentNode.removeChild(lastChild);
            }
          }
          htmlElement.parentNode.prepend(
            document.createComment(' BEGIN NOINDEX ')
          );
          htmlElement.parentNode.append(
            document.createComment(' END NOINDEX ')
          );
        }
        this.commentCreated = true;
      } catch (e) {
        // Most likely this was thrown in SSR.
      }
    }
  }

  filterChannels() {
    this.channels = this.channelsPrefiltered.filter((message) =>
      this.shouldShowOnPage(message)
    );
  }

  shouldShowOnPage(message: ChannelsType): boolean {
    if (message.pattern.length === 0) {
      return true;
    }

    const regExp = new RegExp(message.pattern.trim());

    return regExp.test(this.router.url);
  }

  bannerLinkAnalytics(href: string) {
    this.analyticsService.trackAlertBanner('Banner link clicked');
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        window.location.href = href;
      }, 2000);
    }
  }

  getAlerts(): void {
    this.cmsContentService
      .getBannerAlerts(this.useMockServiceAlertsData)
      .pipe(take(1))
      .subscribe((data: ChannelsType[]) => {
        if (this.limitSingleMessage) {
          this.channels = [this.mockServiceAlertsData[0]];
        } else {
          this.channelsPrefiltered = data;
          this.filterChannels();
        }
        this.alertBannerLoaded();
      });
  }

  handleCloseAlerts(): void {
    this.showBanner = false;
    this.analyticsService.trackAlertBanner('Banner Closed');
    this.cmsServiceAlertsService.reset();
  }

  hideChannel() {
    this.showMobileAlerts = !this.showMobileAlerts;
    this.applyAlertBannerLinksAnalytics();
    this.analyticsService.trackAlertBanner('Banner link clicked');
  }

  alertBannerLoaded() {
    if (this.channels?.length > 0) {
      setTimeout(() => {
        this.applyAlertBannerLinksAnalytics();
        this.analyticsService.trackAlertBanner('Banner Seen');
      }, 12000);
    }
  }

  bannerClickAnalytics() {
    setTimeout(() => {
      this.analyticsService.trackAlertBanner('Banner link clicked');
    }, 100);
  }

  applyAlertBannerLinksAnalytics() {
    if (isPlatformBrowser(this.platformId)) {
      document.querySelectorAll('.alert-html').forEach((el) => {
        el.querySelectorAll('a').forEach((a) => {
          a.removeEventListener('click', this.bannerClickAnalytics);
          a.addEventListener('click', this.bannerClickAnalytics);
        });
      });
    }
  }

  onSlide(slideEvent: NgbSlideEvent): void {
    this.currentSlide = this.getCurrentSlideNumber(slideEvent);
  }

  // Bootstrap carousel does not have an event that passes the current slide number, so we have to extrapolate it
  getCurrentSlideNumber(slideEvent: NgbSlideEvent): number {
    const arraySplitByHyphen = slideEvent.current.split('-');
    const numberPosition = arraySplitByHyphen.length - 1;
    const currentSlideNumber = parseInt(arraySplitByHyphen[numberPosition], 10);

    // Sometimes we'll get stuff like "8 of 2" in the slide counter and I don't know why, so I did this
    if (currentSlideNumber > this.channels.length) {
      return 0;
    }

    return currentSlideNumber;
  }

  pauseCarousel(shouldPause: boolean): void {
    if (shouldPause) {
      this.carousel.pause();
    } else {
      this.carousel.cycle();
    }
  }

  nextMessage(event: Event): void {
    event.preventDefault();

    this.carousel.next();
    this.applyAlertBannerLinksAnalytics();
    this.analyticsService.trackAlertBanner('Banner link clicked');
  }

  previousMessage(event: Event): void {
    event.preventDefault();

    this.carousel.prev();
    this.applyAlertBannerLinksAnalytics();
    this.analyticsService.trackAlertBanner('Banner link clicked');
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
